// A record in conflict (someone else saved it in CRM first): let the measurer
// choose — CRM's version, or theirs over it (#394). Each choice looks at CRM's
// copy first and asks before anything is lost, in the app's centred question
// (#443). ConflictButtons are the choice itself, used by the area and building
// (ConflictChoices, below) and by every row of the held-back list (#693).
import { useRef, useState } from 'react';
import { Button, ConfirmDialog } from '@higgins/ui';
import { isConnectionFailure } from '../lib/api';
import { whyNotMine, type CrmVersion, type FieldChange } from '../lib/conflict-diff';
import { loadCrmVersion, sendMineAnyway, takeCrmVersion } from '../lib/conflicts';
import type { DeviceRecord } from '../lib/device-db';
import { useOnline } from '../lib/hooks';
import { ErrorMessage, Notice } from './message';

export type Choice = 'crm' | 'mine';

/** A choice and what CRM holds, once looked at. */
interface Ask {
  choice: Choice;
  crm: CrmVersion;
}

type Step = { kind: 'idle' } | { kind: 'loading'; choice: Choice } | { kind: 'confirm' } | { kind: 'working' };

function partsText(changes: FieldChange[]): string {
  return changes.length ? changes.map((c) => c.label).join(', ') : 'nothing (both copies are the same)';
}

function question(choice: Choice, crm: CrmVersion, name: string): string {
  if (crm.kind === 'removed') return `Someone removed ${name} in CRM. Drop it from this device? Your changes to it, and its photos not yet uploaded, are lost.`;
  if (choice === 'crm') return `Use CRM's version of ${name}? Your changes on this device to ${partsText(crm.changes)} are lost.`;
  return `Send your version of ${name}? It replaces the other person's changes in CRM to ${partsText(crm.changes)}.`;
}

/** The question's title and its yes. */
function answers(choice: Choice, crm: CrmVersion, what: string): { title: string; yes: string } {
  if (crm.kind === 'removed') return { title: `Drop this ${what}?`, yes: 'Drop it' };
  return choice === 'crm' ? { title: "Use CRM's version?", yes: "Use CRM's" } : { title: 'Send your version?', yes: 'Send mine' };
}

function describe(err: unknown): string {
  if (isConnectionFailure(err)) return 'This needs signal.';
  return err instanceof Error ? err.message : 'That did not work. Try again.';
}

interface ButtonsProps {
  record: DeviceRecord;
  /** The kind of record, in the question's words ("area", "option"). */
  what: string;
  name: string;
  labels: Record<Choice, string>;
  /** False when CRM's copy is known to refuse "mine" (removed, a locked option): only CRM's is offered. */
  mineOffered?: boolean;
  /** Called before the device's copy changes: a keystroke still waiting is saved (keep) or dropped. */
  onResolve?: (keep: boolean) => Promise<void>;
  /** Called once the choice is made: CRM's copy is on the device, or mine is queued — not yet sent (held-back-outcome.ts). */
  onDone?: (choice: Choice) => void;
}

const settled = async () => {};

export function ConflictButtons({ record, what, name, labels, mineOffered = true, onResolve = settled, onDone }: ButtonsProps) {
  const online = useOnline();
  const [step, setStep] = useState<Step>({ kind: 'idle' });
  const [error, setError] = useState<string | null>(null);
  // The last question asked: kept after it closes, so the dialog keeps its words while it fades.
  const [ask, setAsk] = useState<Ask | null>(null);
  const buttons = { crm: useRef<HTMLButtonElement>(null), mine: useRef<HTMLButtonElement>(null) };

  async function look(choice: Choice) {
    setError(null);
    setStep({ kind: 'loading', choice });
    try {
      // Sending mine: save a keystroke still waiting first, so the comparison includes it.
      if (choice === 'mine') await onResolve(true);
      const crm = await loadCrmVersion(record);
      const refused = choice === 'mine' ? whyNotMine(record.table, crm) : null;
      if (refused) {
        setError(refused);
        setStep({ kind: 'idle' });
        return;
      }
      setAsk({ choice, crm });
      setStep({ kind: 'confirm' });
    } catch (err) {
      setError(describe(err));
      setStep({ kind: 'idle' });
    }
  }

  async function go({ choice, crm }: Ask) {
    setStep({ kind: 'working' });
    try {
      await onResolve(choice === 'mine');
      if (choice === 'mine' && crm.kind === 'changed') await sendMineAnyway(record, crm);
      else await takeCrmVersion(record, crm);
      onDone?.(choice);
    } catch (err) {
      setError(describe(err));
      setStep({ kind: 'idle' });
    }
  }

  const busy = step.kind !== 'idle';
  const choiceButton = (choice: Choice) => (
    <Button key={choice} ref={buttons[choice]} size="sm" variant="outline" onClick={() => void look(choice)} disabled={!online || busy}>
      {step.kind === 'loading' && step.choice === choice ? 'Checking…' : labels[choice]}
    </Button>
  );
  return (
    <>
      <div className="mt-2 flex flex-wrap gap-2">
        {/* In the labels' order: the area's screen has CRM's first, as it always has. */}
        {(Object.keys(labels) as Choice[]).filter((choice) => choice === 'crm' || mineOffered).map(choiceButton)}
      </div>
      {ask && (
        <ConfirmDialog
          open={step.kind === 'confirm' || step.kind === 'working'}
          onOpenChange={(open) => !open && setStep({ kind: 'idle' })}
          title={answers(ask.choice, ask.crm, what).title}
          description={question(ask.choice, ask.crm, name)}
          confirmLabel={answers(ask.choice, ask.crm, what).yes}
          busyLabel="Working…"
          destructive
          busy={step.kind === 'working'}
          onConfirm={() => void go(ask)}
          returnFocus={buttons[ask.choice]}
        />
      )}
      {!online && <p className="mt-1 text-xs">Choosing needs signal. Your version stays on this device until then.</p>}
      {error && <ErrorMessage className="mt-2">{error}</ErrorMessage>}
    </>
  );
}

const AREA_LABELS: Record<Choice, string> = { crm: "Use CRM's version", mine: 'Send mine anyway' };

interface Props {
  area: DeviceRecord;
  onResolve: (keep: boolean) => Promise<void>;
}

/** On an area or building in conflict: say so, and the choice. */
export function ConflictChoices({ area, onResolve }: Props) {
  const what = area.table === 'building' ? 'building' : 'area';
  return (
    <Notice>
      <p className="text-xs">
        Someone else changed this {what} in CRM. Your version is kept on this device and not sent until you choose.
      </p>
      <ConflictButtons record={area} what={what} name={String(area.fields.hig_name ?? `this ${what}`)} labels={AREA_LABELS} onResolve={onResolve} />
    </Notice>
  );
}
